import type { ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'
import { Loader, Popover } from '@systemeio/components'
import { useTranslation } from 'next-i18next'
import { useCommentLikes } from 'modules/comments/api/get-comment-likes'
import type { LikeInterface } from 'modules/comments/api/types'
import { usePostLikes } from 'modules/community/api/get-post-likes'

interface LikeDetailPopoverProps {
  likesCount: number
  postId?: number
  commentId?: number
  referenceElement: HTMLElement | null
}

const MAX_DISPLAYED_LIKES = 7

export const LikeDetailPopover = ({
  likesCount,
  referenceElement,
  postId,
  commentId,
}: LikeDetailPopoverProps): ReactNode => {
  const { data: postLikes, isLoading: isPostLoading } = usePostLikes({
    postId: postId!,
    enabled: !!postId,
  })
  const { data: commentLikes, isLoading: isCommentLoading } = useCommentLikes({
    commentId: commentId!,
    enabled: !!commentId,
  })

  if (!postId && !commentId) {
    Sentry.captureMessage('LikeDetailPopover: either postId or commentId must be provided', {
      level: 'error',
    })
    return null
  }

  const isLoading = isPostLoading || isCommentLoading
  const likes = postLikes || commentLikes || []

  const overflowLikes = likesCount > MAX_DISPLAYED_LIKES ? likesCount - MAX_DISPLAYED_LIKES : 0

  return (
    <Popover.Root open>
      <Popover.Portal>
        <Popover.Positioner anchor={referenceElement} side="top">
          <Popover.Popup className="z-10">
            <div className="flex w-fit max-w-[180px] min-w-[120px] flex-col items-center">
              {isLoading ? (
                <Loader />
              ) : (
                <LikesDetailContent likes={likes} overflowLikes={overflowLikes} />
              )}
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

function LikesDetailContent({
  likes,
  overflowLikes,
}: {
  likes: LikeInterface[]
  overflowLikes: number
}): ReactNode {
  const { t } = useTranslation()
  return (
    <>
      {likes?.slice(0, MAX_DISPLAYED_LIKES).map(like => (
        <p className="truncate text-xs not-first:pt-2.5" key={like.createdAt + like.displayName}>
          {like.displayName}
        </p>
      ))}
      {overflowLikes > 0 && (
        <p className="pt-2.5 text-xs whitespace-nowrap text-text-primary/60">
          {t('post.likes.popup.and_others', { count: overflowLikes })}
        </p>
      )}
    </>
  )
}
